import React, { useState, useEffect } from "react";
import { Button, Input, Form, Col, Row, Table } from "reactstrap";
import FontAwesomeButton from '../../../misc/FontAwesomeButton';
import { useTranslation } from "react-i18next";
import { AddClassroomMembersType, AddedClassroomMember, ClassroomResult, CoreClassroomAdminClassroom, CoreClassroomAdminClassroomListing } from "../../../../models/CoreClassroomAdminClassroom";
import { RequestState } from "../../../../models/RequestState";
import { RootState } from "../../../../store/store";
import { ClassroomAddMembers, ClearClassroomAddMembers } from "../../../../store/ClassroomAdmin/ClassroomToolsAction";
import { useDispatch, useSelector } from "react-redux";
import StatusCode from "../../../../util/StatusCode";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from "../../../misc/Loading";


interface ClassroomViewToolAddMembersProps {
    type?: AddClassroomMembersType
    classrooms: CoreClassroomAdminClassroom[] | CoreClassroomAdminClassroomListing[]
}

interface ClassroomMemberResult extends ClassroomResult {
    data?: AddedClassroomMember[]
}

const ClassroomViewToolAddMembers = (props: ClassroomViewToolAddMembersProps) => {
    const { t } = useTranslation(['common', "classroomAdmin"]);
    const dispatch = useDispatch();

    const [members, setMembers] = useState<string[]>([]);
    const [isAddingMember, setIsAddingMember] = useState<boolean>(false);
    const [newMemberValue, setNewMemberValue] = useState<string>("");
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [confirmed, setConfirmed] = useState<boolean>(false);

    const addedMembers = useSelector<RootState, RequestState<AddedClassroomMember[]>>(x => x.classroomTools.addMembersClassroom);

    const [addMembersClassroomState, setAddMembersClassroomState] = useState<ClassroomMemberResult[]>([]);

    useEffect(() => {
        const state = props.classrooms.map(x => ({
            classroomId: x.classroomId,
            code: StatusCode.NONE,
        }));
        setAddMembersClassroomState(state);

        return () => {
            ClearClassroomAddMembers()(dispatch);
            setMembers([]);
        };
    }, []);

    const startEditMember = (index: number) => {
        setEditingIndex(index);
        setEditingValue(members[index]);
    };

    const saveEditedMember = () => {
        if (editingIndex === null) {
            return;
        }

        setMembers(prev => prev.map((member, index) => index === editingIndex ? editingValue : member));
        setEditingIndex(null);
        setEditingValue('');
        setIsAddingMember(true);
    };

    const removeMember = (indexToRemove: number) => {
        setMembers(prev => prev.filter((_, index) => index !== indexToRemove));

        if (editingIndex === null) {
            return;
        }

        if (editingIndex === indexToRemove) {
            setEditingIndex(null);
            setEditingValue('');
            return;
        }

        if (editingIndex > indexToRemove) {
            setEditingIndex(editingIndex - 1);
        }
    };

    const addMember = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!newMemberValue.trim()) {
            return;
        }

        setMembers(prev => [...prev, newMemberValue.trim()]);
        setNewMemberValue('');
        setIsAddingMember(true);
    };

    const addMembers = async () => {
        if (!members.length) {
            return;
        }
        if (props.type === undefined) {
            return;
        }

        setConfirmed(true);
        const memberType = props.type;

        const state = props.classrooms.map(x => ({
            classroomId: x.classroomId,
            code: StatusCode.PENDING,
        }));
        setAddMembersClassroomState(state);

        const results = await Promise.all(
            props.classrooms.map(x =>
                ClassroomAddMembers(x.classroomId, { type: memberType, members: members })(dispatch))
        );

        setAddMembersClassroomState(results);
    }

    const showAddInput = members.length === 0 || isAddingMember || addedMembers.code === StatusCode.COMPLETE;

    const isNewMemberValueValid = newMemberValue.trim().length >= 5;
    const isEditingValueValid = editingValue.trim().length >= 5;
    const hasOnlyValidMembers = members.every(member => member.trim().length >= 5);

    const renderAddMembers = () =>
    (<>
        <Row>
            <Col md="6">
                <Table striped>
                    <thead><tr><th>{t('classroomAdmin.title', { ns: 'classroomAdmin' })}</th></tr></thead>
                    <tbody>
                        {props.classrooms.map(x => (
                            <tr key={x.classroomId}><td>{x.classroomName}</td></tr>
                        ))}
                    </tbody>
                </Table>
            </Col>

            <Col md="6">
                <p><b>{t('tools.ADD_MEMBERS.members', { ns: 'classroomAdmin' })}</b></p>
                {members.map((member, index) => {
                    return (<div key={`${member}-${index}`}>
                        {editingIndex === index ?
                            <Form onSubmit={() => saveEditedMember()} className="flex space-between mb-3 align-items-center">
                                <Input
                                    type="text"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    placeholder={t('tools.ADD_MEMBERS.placeHolder', { ns: 'classroomAdmin' })}
                                />
                                <div className="flex flex-row align-items-center">
                                    <Button type="submit" disabled={!isEditingValueValid} color="primary" className="ms-2">{t('action.save', { ns: 'common' })}</Button>
                                    <Button color="secondary" className="ms-2" onClick={() => (setEditingIndex(null), setIsAddingMember(true))}>{t('action.cancel', { ns: 'common' })}</Button>
                                </div>
                            </Form>
                            : <div className="flex space-between align-items-center border-bottom ps-2 pe-2 pt-2 pb-2">
                                <span>{member}</span>
                                <div className="flex flex-row align-items-center">
                                    <span className="me-3"><FontAwesomeButton icon="pen" onClick={() => { startEditMember(index); setIsAddingMember(false) }} title={t('tools.ADD_MEMBERS.edit', { ns: 'classroomAdmin' })} /></span>
                                    <FontAwesomeButton icon="trash" onClick={() => { removeMember(index); setIsAddingMember(true) }} title={t('action.remove', { ns: 'common' })} />
                                </div>
                            </div>
                        }
                    </div>)
                }
                )}

                {showAddInput ? (<>
                    <Form onSubmit={(e) => addMember(e)} className="flex space-between align-items-center mt-3">
                        <Input
                            type="text"
                            value={newMemberValue}
                            onChange={(e) => setNewMemberValue(e.target.value)}
                            placeholder={t('tools.ADD_MEMBERS.placeHolder', { ns: 'classroomAdmin' })}
                        />
                        <Button color="primary" disabled={!isNewMemberValueValid} className="ms-3 text-nowrap" type="submit">{t('action.add', { ns: 'common' })}</Button>
                    </Form>
                </>)
                    : <Button color="secondary" onClick={() => { setIsAddingMember(true); setEditingIndex(null) }}>+</Button>}
            </Col>
        </Row>

        <div className="flex align-items-center mt-3">
            <Button onClick={() => addMembers()} disabled={members.length < 1 || addedMembers.code !== StatusCode.NONE || !hasOnlyValidMembers || editingIndex !== null} className="me-3">
                {props.type === AddClassroomMembersType.Student ? t('tools.ADD_MEMBERS.studentTitle', { ns: 'classroomAdmin' })
                    : props.type === AddClassroomMembersType.Teacher ? t('tools.ADD_MEMBERS.teacherTitle', { ns: 'classroomAdmin' })
                        : null}
            </Button>
        </div>
    </>);

    const renderStatus = (classroomId: string) => {
        var classroom = addMembersClassroomState.find(x => x.classroomId === classroomId);
        if (!classroom) {
            return;
        }
        if (classroom.code === StatusCode.PENDING) {
            return <Loading />;
        }

        if (classroom.code === StatusCode.COMPLETE) {
            if (!classroom.data) {
                return;
            }
            if (classroom.data.every(x => x.success)) {
                return <FontAwesomeIcon icon="check" color="green" style={{ marginRight: '5px' }} />
            }
            if (classroom.data.some(x => x.success)) {
                return <FontAwesomeIcon icon="triangle-exclamation" color="orange" style={{ marginRight: '5px' }} />
            }

            if (classroom.data.every(x => !x.success)) {
                return <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
            }
        }
        if (classroom.code === StatusCode.ERROR) {
            const errorData = classroom.error?.response?.data as { title?: string; detail?: string } | undefined;
            const title = errorData?.title;
            var errorMessage = title ? t(`tools.errors.general.${title}`, { ns: 'classroomAdmin' }) : t(`error.somethingWentWrong`, { ns: 'common' });
            return (<>
                <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
                {errorMessage}
            </>);
        }
    }

    const getErrorMessage = (error?: string) => {
        if (!error) {
            return;
        }
        return (t(`tools.errors.members.${error}`, { ns: 'classroomAdmin' }))
    }

    const canToggleRow = (classroomId: string): boolean => {
        const classroom = addMembersClassroomState.find(x => x.classroomId === classroomId);
        if (!classroom) {
            return false;
        }
        if (classroom.code === StatusCode.COMPLETE) {
            if (!classroom.data) return false;
            if (classroom.data.some(x => x.success)) return true;
            if (classroom.data.every(x => !x.success)) return true;
            return false;
        }
        return false;
    }

    const renderAddMembersResult = () => (<div className="classroom-tool-add-member">
        <div className="header">
            <span>{t('tools.tableHeader.classroom', { ns: 'classroomAdmin' })}</span>
            <span>{t('tools.tableHeader.status', { ns: 'classroomAdmin' })}</span>
            <span></span>
        </div>
        {props.classrooms.map(x => {
            const canToggle = canToggleRow(x.classroomId);
            const memberState = addMembersClassroomState.find(cs => cs.classroomId === x.classroomId)

            return (<React.Fragment key={x.classroomId}>
                <div className="body" key={x.classroomId} onClick={() => setExpandedRows({ ...expandedRows, [x.classroomId]: !expandedRows[x.classroomId] })} >
                    <span>{x.classroomName}</span>
                    <span>{renderStatus(x.classroomId)}</span>
                    <span>{canToggle ? expandedRows[x.classroomId] ?
                        <FontAwesomeIcon icon="chevron-down" />
                        : <FontAwesomeIcon icon="chevron-right" />
                        : null}</span>
                </div>

                {canToggle && expandedRows[x.classroomId] && (<div className="expandedRow">
                    <div className="header">
                        <span>{t('tools.ADD_MEMBERS.addedMembersResult.expandedRowHeaders.member', { ns: 'classroomAdmin' })}</span>
                        <span>{t('tools.ADD_MEMBERS.addedMembersResult.expandedRowHeaders.status', { ns: 'classroomAdmin' })}</span>
                        <span>{t('tools.ADD_MEMBERS.addedMembersResult.expandedRowHeaders.error', { ns: 'classroomAdmin' })}</span>
                    </div>
                    {memberState?.data?.map((user, index) => (
                        <div key={index} className="body">
                            <span>{user.email}</span>
                            <span>{user.success ?
                                <FontAwesomeIcon icon="check" color="green" style={{ marginRight: '5px' }} />
                                : <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
                            }</span>
                            <span>{getErrorMessage(user?.error)}</span>
                        </div>
                    ))}
                </div>)
                }
            </React.Fragment>)
        })}
    </div>);

    return (<>
        {!confirmed && renderAddMembers()}
        {confirmed && renderAddMembersResult()}
    </>)
}

export default ClassroomViewToolAddMembers;
